import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Stack } from '@mui/material';
import { ChipTabs, Paper } from '@shared/components';
import { PageLayout, SuspenseWithSentryErrorBoundary } from '@retail/components';
import {
  CustomerAssortmentPricesContainer,
  DiscountMatrixContainer,
} from '@retail/calculus/containers';
import { CreateCustomerContainer } from '@retail/customer-discount/containers';
import { useAppTFunction } from '@retail/app/i18n';
import { mgPrisRoutes } from '@retail/mgpris/config';

const discountAreaRoute = mgPrisRoutes.discounts.discountArea;

export enum DiscountTab {
  DISCOUNT_MATRIX = 'discountMatrix',
  CUSTOMER_PRICES = 'customerPrices',
}

export function DiscountsPage() {
  const t = useAppTFunction();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const tabs = useMemo(
    () => [
      { label: t('app.routes.discountMatrix'), value: DiscountTab.DISCOUNT_MATRIX },
      { label: t('app.routes.customerPrices'), value: DiscountTab.CUSTOMER_PRICES },
    ],
    [t]
  );

  const urlDiscountArea = pathname.split('/').filter(Boolean).pop() as DiscountTab;
  const discountArea = tabs.find((tab) => tab.value === urlDiscountArea)?.value;

  if (!discountArea)
    return (
      <Navigate
        to={discountAreaRoute.getFullLinkPath({ discountArea: DiscountTab.DISCOUNT_MATRIX })}
        replace
      />
    );

  return (
    <PageLayout maxWidth="full" fixedHeight spacing={2}>
      <Stack height="100%" overflow="hidden">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <ChipTabs
            id="discounts"
            tabs={tabs}
            currentTab={discountArea}
            onChange={(_, value) =>
              navigate(discountAreaRoute.getFullLinkPath({ discountArea: value }))
            }
          />
          <CreateCustomerContainer />
        </Stack>
        <Paper sx={{ display: 'flex', height: '100%', overflow: 'auto', p: 0 }}>
          <Stack flexGrow={1} overflow="hidden">
            <SuspenseWithSentryErrorBoundary>
              {discountArea === DiscountTab.DISCOUNT_MATRIX && <DiscountMatrixContainer />}
              {discountArea === DiscountTab.CUSTOMER_PRICES && (
                <CustomerAssortmentPricesContainer />
              )}
            </SuspenseWithSentryErrorBoundary>
          </Stack>
        </Paper>
      </Stack>
    </PageLayout>
  );
}
